<template>
  <v-dialog v-model="internalValue" max-width="900px" scrollable>
    <v-card>
      <v-toolbar dark color="primary" class="elevation-0">
        <v-btn icon dark @click="internalValue = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Comunicaciones</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="ma-0"
        ></v-progress-linear>

        <template v-else>
          <!-- ── Summary ─────────────────────────────────────────────────── -->
          <div class="summary-section pa-4">
            <v-layout row wrap>
              <v-flex xs12 md7>
                <div class="title font-weight-bold mb-1">{{ user.name }}</div>
                <div class="body-2 grey--text text--darken-2 mb-1">
                  <span>
                    <v-icon small class="mr-1" style="vertical-align:middle">
                      mdi-email-outline
                    </v-icon>
                    {{ user.email }}
                  </span>
                </div>
                <div v-if="user.created_at" class="caption grey--text text--darken-1 mb-2">
                  <span>
                    <v-icon small class="mr-1" style="vertical-align:middle">
                      mdi-calendar-plus
                    </v-icon>
                    Alta {{ formatD(user.created_at) }}
                  </span>
                </div>
                <div class="mb-1">
                  <v-chip
                    v-for="role in user.roles"
                    :key="role"
                    small
                    label
                    color="blue-grey lighten-4"
                    text-color="blue-grey darken-3"
                    class="mr-1 mb-1"
                  >
                    {{ roleLabel(role) }}
                  </v-chip>
                  <span v-if="!user.roles || !user.roles.length" class="caption grey--text">
                    Sin roles
                  </span>
                </div>
                <div
                  v-if="user.companies && user.companies.length"
                  class="caption grey--text text--darken-1"
                >
                  <v-icon small class="mr-1" style="vertical-align:middle">mdi-domain</v-icon>
                  {{ user.companies.join(', ') }}
                </div>
              </v-flex>

              <v-flex xs12 md5 class="pl-md-4 pt-3 pt-md-0">
                <div class="progress-row">
                  <div class="caption grey--text text--darken-1 mb-1 font-weight-medium">
                    Onboarding Openges
                  </div>
                  <template v-if="onboardingProgress && onboardingProgress.total > 0">
                    <div class="caption mb-1">
                      {{ onboardingProgress.done }}/{{ onboardingProgress.total }}
                    </div>
                    <v-progress-linear
                      :value="onboardingProgressPercentage"
                      height="6"
                      color="primary"
                      background-color="grey lighten-3"
                    ></v-progress-linear>
                  </template>
                  <span v-else class="caption grey--text">—</span>
                </div>
              </v-flex>
            </v-layout>
          </div>

          <v-divider></v-divider>

          <!-- user_created: shown as origin marker, not a regular step -->
          <div v-if="userCreatedStep" class="origin-marker px-4 py-2">
            <v-icon small color="grey darken-1" class="mr-1">mdi-flag-checkered</v-icon>
            <span class="caption grey--text text--darken-2">
              Punto de inicio -
              {{ userCreatedStep.date ? formatDT(userCreatedStep.date) : 'Sin fecha registrada' }}
            </span>
          </div>
          <v-divider v-if="userCreatedStep"></v-divider>

          <v-list two-line class="py-1">
            <template v-for="(step, idx) in onboardingMainSteps">
              <v-list-tile :key="idx" class="step-tile">
                <v-list-tile-action class="step-icon-col">
                  <v-icon :color="iconColor(step.status)">{{ stepIcon(step.status) }}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title class="step-title">
                    {{ step.label }}
                    <v-chip
                      small
                      label
                      :color="chipColor(step.status)"
                      :text-color="chipTextColor(step.status)"
                      class="ml-2 ma-0"
                    >
                      {{ statusLabel(step.status) }}
                    </v-chip>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    <div v-if="step.subject" class="subject-line">Asunto: {{ step.subject }}</div>
                    <div>
                      <span v-if="step.date">{{ formatDT(step.date) }}</span>
                      <span v-else-if="step.expected_date">
                        Prevista: {{ formatD(step.expected_date) }}
                      </span>
                      <span v-else class="grey--text">-</span>
                    </div>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action
                  v-if="step.preview_url || canSendEmail(step) || canMarkAsSent(step)"
                  class="step-actions"
                >
                  <v-tooltip left v-if="step.preview_url">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="grey darken-2"
                        class="action-icon-btn preview-icon-btn"
                        v-on="on"
                        @click.stop="$emit('open-preview', step.preview_url)"
                      >
                        <v-icon small>mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver email</span>
                  </v-tooltip>
                  <v-tooltip left v-if="canSendEmail(step)">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        class="action-icon-btn send-icon-btn"
                        :loading="isSendingEmail(step)"
                        :disabled="!!sendingAction"
                        v-on="on"
                        @click.stop="$emit('send-email', step)"
                      >
                        <v-icon small>mdi-send</v-icon>
                      </v-btn>
                    </template>
                    <span>Enviar email</span>
                  </v-tooltip>
                  <v-tooltip left v-if="canMarkAsSent(step)">
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="success"
                        class="action-icon-btn mark-icon-btn"
                        :loading="isMarkingAsSent(step)"
                        :disabled="!!sendingAction"
                        v-on="on"
                        @click.stop="$emit('mark-as-sent', step)"
                      >
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                    </template>
                    <span>Marcar como enviada</span>
                  </v-tooltip>
                </v-list-tile-action>
              </v-list-tile>
              <v-divider
                v-if="idx < onboardingMainSteps.length - 1"
                :key="'d' + idx"
                inset
              ></v-divider>
            </template>
            <v-list-tile v-if="!onboardingMainSteps.length">
              <v-list-tile-content>
                <v-list-tile-title class="grey--text caption pa-4">
                  Sin acciones registradas ni pendientes
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';

const ROLE_LABELS = {
  admin: 'Admin',
  all: 'Todos',
  all_except_lopd_only: 'Todos salvo solo LOPD',
  autonomous: 'Autónomo',
  consultant: 'Asesores',
  pyme: 'Pyme',
  superadmin: 'Super Admin',
};

const EMPTY_CAMPAIGN = { steps: [], progress: null };

export default {
  name: 'UserActionTimelineDialog',
  props: {
    value: { type: Boolean, default: false },
    timeline: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    sendingAction: { type: String, default: null },
    actionMode: { type: String, default: null },
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    user() {
      return this.timeline && this.timeline.user ? this.timeline.user : {};
    },
    onboarding() {
      return this.campaignByKey(['onboarding', 'openges_onboarding'], EMPTY_CAMPAIGN);
    },
    onboardingProgress() {
      return (this.onboarding && this.onboarding.progress) || this.user.progress_onboarding || null;
    },
    onboardingProgressPercentage() {
      const progress = this.onboardingProgress;
      if (!progress || !progress.total) return 0;
      if (typeof progress.percentage !== 'undefined') return progress.percentage;
      return Math.round((progress.done / progress.total) * 100);
    },
    userCreatedStep() {
      return this.onboarding.steps.find(s => s.action === 'user_created') || null;
    },
    onboardingMainSteps() {
      return this.onboarding.steps.filter(s => s.action !== 'user_created');
    },
  },
  methods: {
    campaignByKey(keys, fallback) {
      const campaigns = this.timeline ? this.timeline.campaigns : null;
      if (!campaigns) return fallback;

      if (Array.isArray(campaigns)) {
        return campaigns.find(campaign => keys.indexOf(campaign.key) !== -1) || fallback;
      }

      return keys.reduce((campaign, key) => campaign || campaigns[key], null) || fallback;
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },
    formatDT(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : '—';
    },
    formatD(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '—';
    },
    statusLabel(status) {
      return (
        { completed: 'Completado', in_progress: 'En curso', pending: 'Pendiente' }[status] || status
      );
    },
    chipColor(status) {
      return (
        {
          completed: 'green darken-1',
          in_progress: 'blue darken-1',
          pending: 'grey lighten-2',
        }[status] || 'grey lighten-2'
      );
    },
    chipTextColor(status) {
      return status === 'pending' ? 'grey darken-2' : 'white';
    },
    iconColor(status) {
      return (
        {
          completed: 'green darken-1',
          in_progress: 'blue darken-1',
          pending: 'grey lighten-1',
        }[status] || 'grey lighten-1'
      );
    },
    stepIcon(status) {
      return (
        {
          completed: 'mdi-check-circle-outline',
          in_progress: 'mdi-progress-clock',
          pending: 'mdi-clock-outline',
        }[status] || 'mdi-circle-outline'
      );
    },
    canSendEmail(step) {
      return step && step.status !== 'completed' && step.manual_send_available;
    },
    canMarkAsSent(step) {
      return step && step.status !== 'completed' && step.manual_mark_available;
    },
    isSendingEmail(step) {
      return step && this.sendingAction === step.action && this.actionMode === 'send';
    },
    isMarkingAsSent(step) {
      return step && this.sendingAction === step.action && this.actionMode === 'mark';
    },
  },
};
</script>

<style scoped>
.summary-section {
  background: #fafafa;
}

.origin-marker {
  background: #f9f9f9;
  border-bottom: 1px solid #eeeeee;
}

.step-tile {
  min-height: 64px;
}

.step-icon-col {
  min-width: 40px;
}

.step-actions {
  min-width: 108px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.action-icon-btn {
  box-shadow: none !important;
  height: 30px;
  margin: 0 2px;
  width: 30px;
}

.preview-icon-btn {
  background: #ffffff !important;
  border: 1px solid #bdbdbd;
}

.send-icon-btn {
  background: #1976d2 !important;
  color: #ffffff !important;
}

.send-icon-btn >>> .v-icon {
  color: #ffffff !important;
}

.mark-icon-btn {
  background: #43a047 !important;
  color: #ffffff !important;
}

.mark-icon-btn >>> .v-icon {
  color: #ffffff !important;
}

.step-title {
  white-space: normal;
  height: auto;
  line-height: 1.4;
}

.subject-line {
  color: #616161;
  line-height: 1.35;
  white-space: normal;
}

.progress-row >>> .v-progress-linear {
  margin: 0;
}
</style>
